import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Usuario } from '@/api/auth'
import { InvalidationUtils } from '@/services/CacheInvalidation'
import { cacheService } from '@/services/CacheService'
import { logger } from '@/utils/logger'

export const useSessaoStore = defineStore('sessao', () => {
  const usuario = ref<Usuario | null>(null)
  const sessaoAtiva = ref<boolean>(false)
  const logoutEmAndamento = ref<boolean>(false)

  const estaAutenticado = computed(() => sessaoAtiva.value && !!usuario.value)

  function setUsuario(novoUsuario: Usuario) {
    usuario.value = novoUsuario
    sessaoAtiva.value = true
  }

  function login(novoUsuario: Usuario) {
    setUsuario(novoUsuario)
  }

  function logout(chamarServidor = true) {
    logoutEmAndamento.value = true
    usuario.value = null
    sessaoAtiva.value = false

    try {
      cacheService.clear()
      
      InvalidationUtils.invalidateAuth()
      InvalidationUtils.invalidateCurrentUser()
      InvalidationUtils.invalidateProjects()
      InvalidationUtils.invalidateAdmin()
      
      const keys = Object.keys(sessionStorage)
      keys.forEach(key => {
        if (key.startsWith('preload_')) {
          sessionStorage.removeItem(key)
        }
      })
      
      try {
        import('@/utils/requestCache').then(({ requestCache }) => {
          requestCache.clear()
        }).catch(() => {})
        
        import('@/utils/preloader').then(({ preloader }) => {
          preloader.clearCache()
        }).catch(() => {})
      } catch {
      }
    } catch (error) {
      logger.warn('Erro ao limpar cache no logout', { error })
    }
    
    if (chamarServidor) {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api'
      fetch(`${baseUrl}/auth/logout`, { 
        method: 'POST',
        credentials: 'include'
      }).catch(() => {
      }).finally(() => {
        logoutEmAndamento.value = false
      })
    } else {
      logoutEmAndamento.value = false
    }
  }

  async function verificarSessao() {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api'
      
      const response = await fetch(`${baseUrl}/auth/me`, {
        method: 'GET',
        credentials: 'include'
      })
      
      if (response.ok) {
        const dadosUsuario = await response.json()
        setUsuario(dadosUsuario)
        return true
      } else {
        logout(false)
        return false
      }
    } catch {
      logout(false)
      return false
    }
  }

  return {
    usuario,
    estaAutenticado,
    logoutEmAndamento,
    login,
    logout,
    verificarSessao
  }
})
